import {
  BacklogIcon,
  CalendarIcon,
  CogIcon,
  ComponentIcon,
  EpicIcon,
  FlagIcon,
  ReleaseIcon,
  SprintIcon,
  StatusIcon,
  TimeIcon,
  UserIcon,
} from "app/components/Icons";
import EnvironmentIcon from "app/components/Icons/EnvironmentIcon";
import StoryIcon from "app/components/Icons/StoryIcon";
import NoneIcon from "app/components/Icons/NoneIcon";
import SeverityIcon from "app/components/Icons/SeverityIcon";
import MemberProfileAvatar from "app/components/MemberProfileAvatar";
import TicketTypeIcon from "app/components/TicketTypeIcon";
import { BoardType } from "app/types";
import React from "react";
import { useSelector } from "react-redux";
import { getDueDateLabel } from "utils/helpers";
import { selectDraftBoardForm } from "../../../../components/Settings/redux/selector";
import { getSelectedPlanningGroup } from "../../../Backlog/helper";
import {
  selectPlanningItems,
  selectSelectedPlanningTile,
} from "../../../Backlog/redux/selector";
import { PlanningViewBy } from "../../../Backlog/types";
import TagsPreview from "./TagsPreview";
import { GroupByTypeEnum } from "../../../../types";
import { IGroupHeaderMeta, TicketGroup } from "../../../Tickets/types";

interface Props {
  group: TicketGroup;
  groupBy: GroupByTypeEnum;
  meta: IGroupHeaderMeta;
  className?: string;
  tagsVisibleCount?: number;
}

const GroupLabel: React.FC<Props> = ({
  group,
  groupBy,
  meta,
  className,
  tagsVisibleCount,
}) => {
  const {
    ticketType,
    selectedStatus,
    tagsObj,
    selectedPriority,
    selectedSeverity,
  } = meta;

  const boardFrom = useSelector(selectDraftBoardForm);
  const selectedPlanningTile = useSelector(selectSelectedPlanningTile);
  const { selectedView } = useSelector(selectPlanningItems);
  const wrapper = `flex items-center gap-2 ${className || ""}`;

  const planningGroup = getSelectedPlanningGroup(
    selectedView.viewBy,
    selectedView.groups,
    selectedPlanningTile,
  );

  switch (groupBy) {
    case GroupByTypeEnum.ASSIGNEE:
    case GroupByTypeEnum.CREATOR:
      return group._id ? (
        <MemberProfileAvatar
          id={group._id}
          circleClassName="!w-5 !h-5"
          nameClassName="!font-medium"
        />
      ) : (
        <div className={wrapper}>
          <UserIcon color="#253858" />
          <span className="text-sm font-medium">{group.name}</span>
        </div>
      );

    case GroupByTypeEnum.TAGS:
      return (
        <TagsPreview tags={tagsObj} visibleCount={tagsVisibleCount ?? 2} />
      );

    case GroupByTypeEnum.TICKET_TYPE:
      return ticketType ? (
        <div className={wrapper}>
          <TicketTypeIcon iconName={ticketType.icon} />
          <span className="text-sm font-medium">{ticketType.name}</span>
        </div>
      ) : (
        <span className="text-sm font-medium">No Ticket Type</span>
      );

    case GroupByTypeEnum.STATUS:
      return selectedStatus ? (
        <div className={wrapper}>
          <StatusIcon color={selectedStatus.color} />
          <span
            className="text-sm font-medium"
            style={{ color: selectedStatus.color }}
          >
            {selectedStatus.name}
          </span>
        </div>
      ) : (
        <span className="text-sm font-medium">No Status</span>
      );

    case GroupByTypeEnum.PRIORITY:
      return selectedPriority ? (
        <div className={wrapper}>
          <FlagIcon size={16} color={selectedPriority.color} />
          <span className="text-sm font-medium">{selectedPriority.label}</span>
        </div>
      ) : (
        <div className={wrapper}>
          <FlagIcon size={16} color="#253858" />
          <span className={`${wrapper} text-sm font-medium`}>No Priority</span>
        </div>
      );

    case GroupByTypeEnum.SEVERITY:
      return (
        <div className={wrapper}>
          <SeverityIcon
            size={16}
            color={selectedSeverity?.color ?? "#253858"}
          />
          <span className="text-sm font-medium">
            {selectedSeverity?.id ?? "No Severity"}
          </span>
        </div>
      );

    case GroupByTypeEnum.RELEASE:
      return (
        <div className={wrapper}>
          <ReleaseIcon size={16} color="#253858" />
          <span className="text-sm font-medium">
            {group._id || "No Release"}
          </span>
        </div>
      );

    case GroupByTypeEnum.ENVIRONMENT:
      return (
        <div className={wrapper}>
          <EnvironmentIcon size={20} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px]">
            {group._id || "No Environment"}
          </span>
        </div>
      );

    case GroupByTypeEnum.SPRINT:
      return (
        <div className={wrapper}>
          <SprintIcon size={20} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px]">
            {group._id || "No Sprint"}
          </span>
        </div>
      );

    case GroupByTypeEnum.COMPONENT:
      return (
        <div className={wrapper}>
          <ComponentIcon size={20} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px]">
            {group._id || "No Component"}
          </span>
        </div>
      );

    case GroupByTypeEnum.EFFORTS_ESTIMATION:
    case GroupByTypeEnum.EFFORT_ACTUAL:
      return (
        <div className={wrapper}>
          <TimeIcon size={20} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px]">
            {group._id || "No Effort"}
          </span>
        </div>
      );

    case GroupByTypeEnum.CUSTOM_TITLES:
      return (
        <div className={wrapper}>
          <CogIcon size={20} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px]">
            {group.name || "No Custom Title"}
          </span>
        </div>
      );

    case GroupByTypeEnum.DUE_DATE:
      return (
        <div className={wrapper}>
          <CalendarIcon size={20} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px]">
            {getDueDateLabel(group._id)}
          </span>
        </div>
      );
    case GroupByTypeEnum.CREATED_DATE:
      return (
        <div className={wrapper}>
          <CalendarIcon size={20} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px]">
            {group._id ?? "No created date"}
          </span>
        </div>
      );
    case GroupByTypeEnum.NONE:
      if (boardFrom.Type === BoardType.Backlog && planningGroup) {
        if (selectedView.viewBy === PlanningViewBy.EPICS) {
          const epicColor = planningGroup.color || "#79808A";

          return (
            <div className={wrapper}>
              <EpicIcon size={16} color={epicColor} />
              <span
                className="text-sm font-medium truncate max-w-[130px]"
                style={{ color: epicColor }}
              >
                {planningGroup.name}
              </span>
            </div>
          );
        }

        return (
          <div className={wrapper}>
            <StoryIcon size={16} color="#253858" />
            <span className="text-sm font-medium truncate max-w-[130px] text-secondary">
              {planningGroup.name}
            </span>
          </div>
        );
      }

      return boardFrom.Type === BoardType.Backlog ? (
        <div className={wrapper}>
          <BacklogIcon size={16} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px] text-secondary ">
            Backlog
          </span>
        </div>
      ) : (
        <div className={wrapper}>
          <NoneIcon size={16} color="#253858" />
          <span className="text-sm font-medium truncate max-w-[130px] text-secondary ">
            None
          </span>
        </div>
      );

    default:
      return <span className="text-sm font-medium">{group._id}</span>;
  }
};

export default React.memo(GroupLabel);
